















































import { Component, Prop, Mixins } from 'vue-property-decorator';
import TwoFactorAuthSetup from '~/components/Authentication/TwoFactorAuthSetup.vue';
import TwoFactorBox from '~/components/Authentication/TwoFactorBox.vue';
import disableTwoFa from '~/mutations/disableTwoFa.gql';
import { Action, Mutation, State } from 'vuex-class';
import BrandCard from '~/components/BrandCard.vue';
import TwoFactorAuthInputPrompt from '~/components/ModalPrompts/TwoFactorAuthInputPrompt.vue';
import _isEqual from 'lodash.isequal';
import _omit from 'lodash.omit';
import AuthQuery from '~/mixins/AuthQuery';
import { twoFaErrorCodes } from '~/utils/errorCodes';
import {
  defaultTwoFaCheckpoints,
  IEnabledTwoFaCheckpoints,
  TwoFaCheckpoint,
} from '~/types/two-fa-checkpoints';
import { getSnackbarMessageKeyForTwoFaError } from '~/utils';
import { consumeAccessToken, requestAccessToken } from '~/utils/pkceTokens';

@Component({
  components: {
    TwoFactorAuthSetup,
    TwoFactorBox,
    BrandCard,
    TwoFactorAuthInputPrompt,
  },
})
export default class TwoFa extends Mixins(AuthQuery) {
  @Mutation toggleSuccessSnackbar!: (payload?: boolean) => void;
  @Mutation toggleErrorSnackbar!: (payload?: boolean) => void;
  @Mutation updateSnackbarErrorText!: (args: any) => void;
  @Mutation updateSnackbarSuccessText!: (args: any) => void;
  @Mutation('updateUser', { namespace: 'profile' })
  private updateUserMutation!: (args: any) => void;

  @Action('updateUser', { namespace: 'profile' })
  private updateUserAction!: (args: any) => void;

  @State(profile => profile.user.twoFaEnabled, { namespace: 'profile' })
  private twoFaEnabled!: boolean;

  @State(profile => profile.user.enabledTwoFaCheckpoints, {
    namespace: 'profile',
  })
  private enabledTwoFaCheckpoints!: IEnabledTwoFaCheckpoints;

  @Prop(Function)
  readonly closeDialog!: () => void;

  twoFaQrCode = '';
  twoFaSecret = '';
  twoFaPin = '';
  isFetching = false;
  pendingEnabledTwoFaCheckpoints: IEnabledTwoFaCheckpoints = defaultTwoFaCheckpoints;
  auth0AccessToken = '';

  customTwoFaRestrictions: { name: string; key: TwoFaCheckpoint }[] = [
    {
      name: 'Account Modifications',
      key: TwoFaCheckpoint.accountModifications,
    },
    { name: 'Account Login', key: TwoFaCheckpoint.accountLogin },
    { name: 'Transactions', key: TwoFaCheckpoint.transactions },
    { name: 'Purchases', key: TwoFaCheckpoint.purchases },
    { name: 'Account Deletion', key: TwoFaCheckpoint.accountDeletion },
    {
      name: 'Transfer Code Recovery',
      key: TwoFaCheckpoint.transferCodeRecovery,
    },
    {
      name: 'Download Private Key',
      key: TwoFaCheckpoint.downloadPrivateKey,
    },
    {
      name: 'Connecting/Disconnecting Web3 Wallet',
      key: TwoFaCheckpoint.web3WalletConnection,
    },
  ];

  mounted() {
    this.pendingEnabledTwoFaCheckpoints = { ...this.enabledTwoFaCheckpoints };
    delete (this.pendingEnabledTwoFaCheckpoints as any).__typename;
  }

  created() {
    this.continueTwoFaDisable();
  }

  get updateTwoFaCheckpointsButtonDisabled() {
    return _isEqual(
      _omit(this.enabledTwoFaCheckpoints, '__typename'),
      this.pendingEnabledTwoFaCheckpoints,
    );
  }

  updateTwoFaRestrictions() {
    this.doAuthQuery(async totpToken => {
      await this.updateUserAction({
        enabledTwoFaCheckpoints: { ...this.pendingEnabledTwoFaCheckpoints },
        totpToken,
        rethrowErrorCodes: twoFaErrorCodes,
      });
    });
  }

  async continueTwoFaDisable() {
    if (!this.twoFaEnabled) {
      return;
    }

    const action = this.$route.query.action;

    if (action !== 'disable') {
      return;
    }

    const accessToken = consumeAccessToken();

    if (!accessToken) {
      return;
    }

    this.isFetching = true;
    this.auth0AccessToken = accessToken;

    try {
      await this.$apollo.mutate({
        mutation: disableTwoFa,
        variables: {
          auth0AccessToken: this.auth0AccessToken,
        },
      });

      this.updateUserMutation({ twoFaEnabled: false });
      this.updateSnackbarSuccessText(
        this.$t('components.settings.twoFa.disabledMsg') as string,
      );
      this.toggleSuccessSnackbar();
      this.closeDialog();
    } catch (err) {
      console.warn(err);

      const twoFaErrorSnackbarMessageKey = getSnackbarMessageKeyForTwoFaError(
        err,
      );
      if (twoFaErrorSnackbarMessageKey) {
        this.updateSnackbarErrorText(this.$t(twoFaErrorSnackbarMessageKey));
      } else {
        this.updateSnackbarErrorText(
          this.$t('components.settings.twoFa.errorMsg') as string,
        );
        this.$sentry.captureException(err);
      }
      this.toggleErrorSnackbar();
    } finally {
      this.isFetching = false;
    }
  }

  async disableTwoFa() {
    return requestAccessToken('/account?component=TwoFa&action=disable', [
      'remove:authenticators',
      'read:authenticators',
    ]);
  }

  twoFaValidated() {
    this.updateUserMutation({ twoFaEnabled: true });
    this.updateSnackbarSuccessText(
      this.$t('components.settings.twoFa.enabledMsg') as string,
    );
    this.toggleSuccessSnackbar();

    this.closeDialog();
  }
}
